import { useEffect, useState, useRef } from "react"

import { ChevronRight } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@lib/utils"

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible"
import { Input } from "@ui/input"
import { Label } from "@ui/label"

const DEBOUNCE_DELAY = 1000

interface SearchTextSectionProps {
    section: string
    Icon?: LucideIcon
    onSelection?: (selection: any) => void
    onClear: number
}
export function SearchTextSection({ section, Icon, onSelection, onClear }: Readonly<SearchTextSectionProps>) {
    const [name, setName] = useState<string>("")
    const [fullText, setFullText] = useState<string>("")

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    const handleNameOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target
        setName(value)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            if (value.length === 0 && fullText.length === 0) {
                onSelection?.(undefined)
            } else {
                onSelection?.({
                    fullText: fullText.length === 0 ? undefined : fullText,
                    name: value.length === 0 ? undefined : value
                })
            }
        }, DEBOUNCE_DELAY)
    }

    const handleFullTextOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target
        setFullText(value)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            if (value.length === 0 && name.length === 0) {
                onSelection?.(undefined)
            } else {
                onSelection?.({
                    fullText: value.length === 0 ? undefined : value,
                    name: name.length === 0 ? undefined : name
                })
            }
        }, DEBOUNCE_DELAY)
    }

    const handleOnSelectionChange = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
        onSelection?.({
            fullText: fullText.length === 0 ? undefined : fullText,
            name: name.length === 0 ? undefined : name
        })
    }

    useEffect(() => {
        setName("")
        setFullText("")

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
    }, [onClear])

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    return (
        <div className={cn("relative flex w-full min-w-0 flex-col p-2 border-b border-sidebar-border")}>
            <Collapsible defaultOpen={false} className="group/collapsible">
                <CollapsibleTrigger className={cn("group/label w-full text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", "flex h-8 shrink-0 items-center rounded-md px-2 text-sm font-medium outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 hover:cursor-pointer mb-1")}>
                    {Icon && <Icon className="mr-2" />}
                    {section} <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="flex flex-col gap-2">
                        <div>
                            <Label className="text-sm text-gray-800">Full Text Search</Label>
                            <Input value={fullText} onChange={handleFullTextOnChange} onKeyDown={(e) => e.key === "Enter" && handleOnSelectionChange()} placeholder={"Search by Full Text"} className="bg-white shadow-none hover:shadow-sm" />
                        </div>
                        <div>
                            <Label className="text-sm text-gray-800">Name Search</Label>
                            <Input value={name} onChange={handleNameOnChange} onKeyDown={(e) => e.key === "Enter" && handleOnSelectionChange()} placeholder={"Search by Name or ID"} className="bg-white shadow-none hover:shadow-sm" />
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    )
}
